import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Stack,
  InputGroup,
  InputRightElement,
  useColorModeValue,
} from '@chakra-ui/react';
import { useFormContext, type FieldPathByValue } from 'react-hook-form';
import { FormTextInput } from 'src/components/molecules/formInput/FormTextInput';
import { FormSearchSelect } from 'src/components/molecules/formInput/FormSearchSelect';
import {
  DEFAULT_CURRENCY_CODE,
  SUPPORTED_CURRENCIES,
  getCurrencyLabel,
} from 'src/utils/currency';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { IconButton } from 'src/components/atoms/IconButton';
import {
  validateNewPassword,
  NEW_PASSWORD_HELPER_TEXT,
} from 'src/components/templates/AuthUIContainer/forms/validators';
import { Icon } from 'src/components/atoms/Icon';
import { Country, City } from 'country-state-city';

type AccountDetailFormData = {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;

  // Company Information
  companyName: string;
  companyAddress: string;
  country: {
    value: string;
    label: string;
  };
  currency: {
    value: string;
    label: string;
  };
  city: string;
  customerCategory: string;

  // Product Usage Questions
  yearRoundUsage: string;
  seasonalUsage: string;

  // Project Information
  projectInfo: string;

  // Brand & competitors (share-of-search tracking)
  brandKeyword: string;
  competitorKeywords: string;
};

const ADMIN_WATCH_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'password',
  'confirmPassword',
  'companyName',
  'companyAddress',
  'country',
  'currency',
  'city',
  // 'customerCategory', // selector hidden; defaulted to "general"
  'yearRoundUsage',
  'seasonalUsage',
  'projectInfo',
  'brandKeyword',
  'competitorKeywords',
] as const;

const USER_WATCH_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'password',
  'confirmPassword',
  'companyName',
] as const;

type AccountDetailStepProps = {
  onValidationChange?: (isValid: boolean) => void;
  isJoiningExistingCompany: boolean;
  prefillEmail?: string;
};

export const AccountDetailStep: React.FC<AccountDetailStepProps> = ({
  onValidationChange,
  isJoiningExistingCompany,
  prefillEmail,
}) => {
  const [yearRoundUsage, setYearRoundUsage] = useState('yes');
  const [seasonalUsage, setSeasonalUsage] = useState('yes');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const methods = useFormContext<AccountDetailFormData>();
  const { setValue, watch, formState, getValues } = methods;

  // Watch all required fields
  const watchedFields = !isJoiningExistingCompany
    ? watch(ADMIN_WATCH_FIELDS)
    : watch(USER_WATCH_FIELDS);

  // Check if all required fields are filled
  useEffect(() => {
    const allFieldsFilled = watchedFields.every((field) => {
      // Country and currency are object-valued fields in the watch list;
      // require their `value` to be a non-empty string.
      if (field && typeof field === 'object' && 'value' in field) {
        return typeof field.value === 'string' && field.value.trim() !== '';
      }
      return typeof field === 'string' && field.trim() !== '';
    });

    const noErrors =
      formState.errors.confirmPassword === undefined &&
      formState.errors.password === undefined;

    onValidationChange?.(allFieldsFilled && noErrors);
  }, [
    watchedFields,
    formState.errors.confirmPassword,
    formState.errors.password,
    onValidationChange,
  ]);

  // const bgColor = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  const countryOptions = useMemo(
    () =>
      Country.getAllCountries()
        .map((country) => ({
          value: country.isoCode,
          label: country.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );
  const selectedCountry = watch('country');

  const currencyOptions = useMemo(
    () =>
      SUPPORTED_CURRENCIES.map((c) => ({
        value: c.code,
        label: getCurrencyLabel(c.code),
      })),
    [],
  );

  // Pre-fill currency from the chosen country. A MANUAL currency pick is
  // sticky: the prefill writes with shouldDirty: false, so dirtyFields.currency
  // only becomes true when the user touches the select themselves — after
  // that, changing country no longer clobbers their choice.
  const isCurrencyManuallyChosen = !!formState.dirtyFields.currency;
  useEffect(() => {
    if (!selectedCountry?.value || isCurrencyManuallyChosen) {
      return;
    }
    const countryCurrency = Country.getCountryByCode(
      selectedCountry.value,
    )?.currency;
    const code = SUPPORTED_CURRENCIES.some((c) => c.code === countryCurrency)
      ? (countryCurrency as string)
      : DEFAULT_CURRENCY_CODE;
    setValue(
      'currency',
      { value: code, label: getCurrencyLabel(code) },
      { shouldValidate: true, shouldDirty: false },
    );
  }, [selectedCountry?.value, isCurrencyManuallyChosen, setValue]);

  const cityOptions = useMemo(() => {
    if (!selectedCountry?.value) {
      return [];
    }
    return (
      City.getCitiesOfCountry(selectedCountry?.value)
        ?.map((city) => ({
          value: city.name,
          label: city.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [selectedCountry]);

  useEffect(() => {
    const currentCity = getValues('city');

    if (!selectedCountry) {
      if (currentCity) {
        setValue('city', '', { shouldValidate: true, shouldDirty: true });
      }
      return;
    }

    const cityExists = cityOptions.some((city) => city.value === currentCity);
    if (currentCity && !cityExists) {
      setValue('city', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [selectedCountry, cityOptions, getValues, setValue]);

  const handleYearRoundChange = (value: string) => {
    setYearRoundUsage(value);
    setValue('yearRoundUsage', value);
  };

  const handleSeasonalChange = (value: string) => {
    setSeasonalUsage(value);
    setValue('seasonalUsage', value);
  };

  return (
    <Box as="form">
      <VStack spacing={8} align="stretch" mb={{ base: 12, md: 24 }}>
        {/* Plan Selection Section */}
        {/* <Box
            p={6}
            bg={bgColor}
            borderRadius="lg"
            border="1px solid"
            borderColor="transparent"
            cursor="pointer"
            _hover={{ bg: 'gray.100' }}
            transition="all 0.2s">
            <HStack justify="space-between" align="center">
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold" fontSize="lg" color={textColor}>
                  PROFESSIONAL
                </Text>
                <Text color={secondaryTextColor} fontSize="sm">
                  Lorem ipsum dolor sit amet consectetur.
                </Text>
                <HStack align="baseline" spacing={1}>
                  <Text fontWeight="bold" fontSize="xl" color={textColor}>
                    $7.00
                  </Text>
                  <Text fontSize="sm" color={secondaryTextColor}>
                    /Month
                  </Text>
                </HStack>
              </VStack>
              <Icon
                iconName="triangle-down"
                boxSize={5}
                color={secondaryTextColor}
              />
            </HStack>
          </Box> */}

        {/* Personal Information Section */}
        <VStack spacing={6} align="stretch">
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Personal Information
          </Text>

          <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
            <Box flex={1} w={{ base: '100%', md: 'auto' }}>
              <FormTextInput<AccountDetailFormData>
                inputPath="firstName"
                label="First Name *"
                placeholder="Enter First Name"
                required={true}
                helperText=""
              />
            </Box>
            <Box flex={1} w={{ base: '100%', md: 'auto' }}>
              <FormTextInput<AccountDetailFormData>
                inputPath="lastName"
                label="Last Name *"
                placeholder="Enter Last Name"
                required={true}
                helperText=""
              />
            </Box>
          </Stack>

          <FormTextInput<AccountDetailFormData>
            inputPath="email"
            label="Email *"
            placeholder="Enter Email"
            required={true}
            inputProps={{
              // Email always comes from a verified invite token
              isDisabled: !!prefillEmail,
            }}
            helperText=""
          />

          <Stack
            direction={{ base: 'column', md: 'row' }}
            spacing={4}
            align="flex-start">
            <Box flex={1} w={{ base: '100%', md: 'auto' }}>
              <FormControl isInvalid={!!methods.formState.errors.password}>
                <FormLabel
                  fontFamily="marketingPage"
                  color="content.title"
                  fontWeight={500}
                  mb={3}
                  fontSize="16px">
                  Password *
                </FormLabel>
                <InputGroup>
                  <Input
                    {...methods.register('password', {
                      validate: validateNewPassword,
                    })}
                    placeholder="Enter Password"
                    type={showPassword ? 'text' : 'password'}
                    bg="surface.panel"
                    borderColor={
                      methods.formState.errors.password
                        ? 'red'
                        : 'surface.border'
                    }
                    borderRadius="md"
                    _placeholder={{ color: 'content.muted' }}
                    _focus={{
                      borderColor: methods.formState.errors.password
                        ? 'red'
                        : 'icon.green',
                      boxShadow: methods.formState.errors.password
                        ? '0 0 0 1px #CC6478'
                        : '0 0 0 1px var(--chakra-colors-icon-green)',
                    }}
                    _hover={{
                      borderColor: methods.formState.errors.password
                        ? 'red'
                        : 'surface.border',
                    }}
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      colorScheme="gray">
                      <Icon
                        iconName={showPassword ? 'view-off' : 'view'}
                        color="content.muted"
                      />
                    </IconButton>
                  </InputRightElement>
                </InputGroup>
                {methods.formState.errors.password ? (
                  <FormErrorMessage color="red" fontSize="sm">
                    {methods.formState.errors.password.message}
                  </FormErrorMessage>
                ) : (
                  <Text mt={1} fontSize="xs" color="content.muted">
                    {NEW_PASSWORD_HELPER_TEXT}
                  </Text>
                )}
              </FormControl>
            </Box>
            <Box flex={1} w={{ base: '100%', md: 'auto' }}>
              <FormControl
                isInvalid={!!methods.formState.errors.confirmPassword}>
                <FormLabel
                  htmlFor="confirmPassword"
                  fontFamily="marketingPage"
                  color="content.title"
                  fontWeight={500}
                  mb={3}
                  fontSize="16px">
                  Confirm password *
                </FormLabel>
                <InputGroup>
                  <Input
                    {...methods.register('confirmPassword', {
                      required: 'Confirm password is required',
                      validate: (value) => {
                        const password = methods.watch('password');
                        return value === password || 'Passwords do not match';
                      },
                    })}
                    id="confirmPassword"
                    placeholder="Enter Password Again"
                    type={showConfirmPassword ? 'text' : 'password'}
                    bg="surface.panel"
                    borderColor={
                      methods.formState.errors.confirmPassword
                        ? 'red'
                        : 'surface.border'
                    }
                    borderRadius="md"
                    _placeholder={{ color: 'content.muted' }}
                    _focus={{
                      borderColor: methods.formState.errors.confirmPassword
                        ? 'red'
                        : 'icon.green',
                      boxShadow: methods.formState.errors.confirmPassword
                        ? '0 0 0 1px #CC6478'
                        : '0 0 0 1px var(--chakra-colors-icon-green)',
                    }}
                    _hover={{
                      borderColor: methods.formState.errors.confirmPassword
                        ? 'red'
                        : 'surface.border',
                    }}
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label={
                        showConfirmPassword ? 'Hide password' : 'Show password'
                      }
                      h="1.75rem"
                      size="sm"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      variant="ghost"
                      colorScheme="gray">
                      <Icon
                        iconName={showPassword ? 'view-off' : 'view'}
                        color="content.muted"
                      />
                    </IconButton>
                  </InputRightElement>
                </InputGroup>
                {methods.formState.errors.confirmPassword && (
                  <FormErrorMessage color="red" fontSize="sm">
                    {methods.formState.errors.confirmPassword.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            </Box>
          </Stack>
        </VStack>

        {/* Company Information Section */}
        <VStack spacing={6} align="stretch">
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Company Information
          </Text>

          <FormTextInput<AccountDetailFormData>
            inputPath="companyName"
            label="Company Name *"
            placeholder="Enter Company Name"
            required={true}
            inputProps={{
              isDisabled: isJoiningExistingCompany,
            }}
            helperText=""
          />

          {!isJoiningExistingCompany && (
            <>
              <FormTextInput<AccountDetailFormData>
                inputPath="companyAddress"
                label="Company Address *"
                placeholder="Enter Company Address"
                required={true}
                helperText=""
              />

              <Stack
                direction={{ base: 'column', md: 'row' }}
                spacing={4}
                align="start">
                <Box flex={1} w={{ base: '100%', md: 'auto' }}>
                  <FormSearchSelect<AccountDetailFormData, string>
                    inputPath={
                      'country' as FieldPathByValue<
                        AccountDetailFormData,
                        string
                      >
                    }
                    placeholder="Select Country"
                    options={countryOptions}
                    required={true}
                    label="Country *"
                    selectProps={{
                      noOptionsMessage: () => 'No countries found',
                    }}
                  />
                </Box>
                <Box flex={1} w={{ base: '100%', md: 'auto' }}>
                  <FormSearchSelect<AccountDetailFormData, string>
                    inputPath="city"
                    placeholder="Select City"
                    options={cityOptions}
                    required={true}
                    label="City *"
                    selectProps={{
                      isDisabled: !selectedCountry?.value,
                      noOptionsMessage: () =>
                        selectedCountry?.value
                          ? 'No cities found'
                          : 'Select a country first',
                    }}
                  />
                </Box>
              </Stack>

              <FormSearchSelect<AccountDetailFormData, string>
                inputPath={
                  'currency' as FieldPathByValue<AccountDetailFormData, string>
                }
                placeholder="Select Currency"
                options={currencyOptions}
                required={true}
                label="Currency *"
                selectProps={{
                  noOptionsMessage: () => 'No currencies found',
                }}
              />
              <Text fontSize="sm" color={secondaryTextColor} mt={-2}>
                The currency your marketing data is reported in.
              </Text>
              {/*
                Business Category selector — hidden. All new customers default
                to "general" (set in the form defaultValues). Restore this
                block to let users pick a category again.
              */}
              {/* <FormSearchSelect<AccountDetailFormData, string>
                inputPath="customerCategory"
                placeholder="Select Business Category"
                options={[
                  { value: 'fmcg', label: 'FMCG' },
                  { value: 'b2b', label: 'B2B' },
                  { value: 'retail', label: 'Retail' },
                  { value: 'automotive', label: 'Automotive' },
                  { value: 'banking', label: 'Banking' },
                  { value: 'insurance', label: 'Insurance' },
                  { value: 'health', label: 'Health' },
                  { value: 'general', label: 'General' },
                ]}
                required={true}
                label="Business Category *"
                selectProps={{
                  noOptionsMessage: () => 'No categories found',
                }}
              /> */}
            </>
          )}
        </VStack>

        {/* Product Usage Questions Section */}
        {!isJoiningExistingCompany && (
          <>
            <VStack spacing={6} align="stretch">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Product/Service Usage Questions
              </Text>

              <VStack spacing={4} align="start">
                <Text fontWeight="medium" color={secondaryTextColor}>
                  Does your product/service sell year-round? *
                </Text>
                <Stack direction="row" spacing={6}>
                  <Checkbox
                    isChecked={yearRoundUsage === 'yes'}
                    onChange={() => handleYearRoundChange('yes')}
                    value="yes"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        Yes
                      </Text>
                    }
                  />
                  <Checkbox
                    isChecked={yearRoundUsage === 'no'}
                    onChange={() => handleYearRoundChange('no')}
                    value="no"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        No
                      </Text>
                    }
                  />
                </Stack>
              </VStack>

              <VStack spacing={4} align="start">
                <Text fontWeight="medium" color={secondaryTextColor}>
                  Does your product/service have seasonal or event-driven peaks?
                  *
                </Text>
                <Stack direction="row" spacing={6}>
                  <Checkbox
                    isChecked={seasonalUsage === 'yes'}
                    onChange={() => handleSeasonalChange('yes')}
                    value="yes"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        Yes
                      </Text>
                    }
                  />
                  <Checkbox
                    isChecked={seasonalUsage === 'no'}
                    onChange={() => handleSeasonalChange('no')}
                    value="no"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        No
                      </Text>
                    }
                  />
                  <Checkbox
                    isChecked={seasonalUsage === 'both'}
                    onChange={() => handleSeasonalChange('both')}
                    value="both"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        Both
                      </Text>
                    }
                  />
                </Stack>
              </VStack>
            </VStack>

            {/* Project Information Section */}
            <VStack spacing={6} align="stretch">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Project Information
              </Text>

              <FormControl>
                <FormLabel
                  fontFamily="marketingPage"
                  color="content.title"
                  fontWeight={500}
                  mb={3}
                  fontSize="16px">
                  Information about your project *
                </FormLabel>
                <Textarea
                  placeholder="Enter information about your project"
                  size="md"
                  rows={4}
                  resize="vertical"
                  bg="surface.panel"
                  border="1px solid"
                  borderColor="surface.border"
                  borderRadius="md"
                  _placeholder={{ color: 'content.muted' }}
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}
                  _hover={{
                    borderColor: 'surface.border',
                  }}
                  {...methods.register('projectInfo', { required: 'Required' })}
                />
              </FormControl>
            </VStack>

            {/* Brand & Competitors Section (share-of-search tracking) */}
            <VStack spacing={6} align="stretch">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Your Brand & Competitors
              </Text>
              <Text fontSize="sm" color={secondaryTextColor}>
                We track how often people search for your brand compared to your
                competitors — this powers the long-term brand effect analysis.
                Search interest is measured in the country you selected above.
              </Text>

              <FormControl>
                <FormLabel
                  fontFamily="marketingPage"
                  color="content.title"
                  fontWeight={500}
                  mb={3}
                  fontSize="16px">
                  Your brand name as people search for it *
                </FormLabel>
                <Input
                  placeholder="e.g. Mercedes"
                  size="md"
                  bg="surface.panel"
                  border="1px solid"
                  borderColor="surface.border"
                  borderRadius="md"
                  _placeholder={{ color: 'content.muted' }}
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}
                  _hover={{
                    borderColor: 'surface.border',
                  }}
                  {...methods.register('brandKeyword', {
                    required: 'Required',
                  })}
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  fontFamily="marketingPage"
                  color="content.title"
                  fontWeight={500}
                  mb={3}
                  fontSize="16px">
                  Your main competitors, comma-separated *
                </FormLabel>
                <Textarea
                  placeholder="e.g. BMW, Volkswagen, Audi"
                  size="md"
                  rows={2}
                  resize="vertical"
                  bg="surface.panel"
                  border="1px solid"
                  borderColor="surface.border"
                  borderRadius="md"
                  _placeholder={{ color: 'content.muted' }}
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}
                  _hover={{
                    borderColor: 'surface.border',
                  }}
                  {...methods.register('competitorKeywords', {
                    required: 'Required',
                  })}
                />
              </FormControl>
            </VStack>
          </>
        )}
      </VStack>
    </Box>
  );
};
